import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Row, Col, Offcanvas, Pagination } from 'react-bootstrap';
import { agentService } from '../../services/agentService';
import { getCurrentAgent } from '../../services/auth';
import { toast } from 'react-hot-toast';

import { useTranslation } from 'react-i18next';

const currencyMap = {
    'None': '', 'USD': '$', 'GBP': '£', 'BTC': '₿', 'EUR': '€', 'CHF': '₣',
    'KRW': '₩', 'TRY': '₺', 'INR': '₹', 'CNY': '¥', 'THB': '฿',
    'RUB': '₽', 'UAH': '₴', 'BDT': '৳', 'JPY': '¥', 'ILS': '₪',
    'AED': 'د.إ', 'SAR': 'ر.س'
};

const WithdrawalManagement = () => {
    const { t } = useTranslation();
    const currentAgent = getCurrentAgent();
    const [withdrawals, setWithdrawals] = useState([]);
    const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showFilterDrawer, setShowFilterDrawer] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [reviewData, setReviewData] = useState({ status: 'approved', notes: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);


    const [filters, setFilters] = useState({
        orderNumber: '',
        username: '',
        userId: ''
    });

    const fetchWithdrawals = useCallback(async (page = 1) => {
        try {
            const response = await agentService.getWithdrawals({
                page,
                limit: 20,
                ...filters
            });
            if (response.success) {
                let data = response.withdrawals;
                if (filters.username) data = data.filter(w => w.User?.username?.toLowerCase().includes(filters.username.toLowerCase()));
                setWithdrawals(data);
                setTotalPages(response.pagination.pages);
                setCurrentPage(response.pagination.page);
            }
        } catch (error) {
            toast.error(t('error_fetch_withdrawals'));
        }
    }, [filters, t]);

    useEffect(() => {
        fetchWithdrawals(1);
    }, [fetchWithdrawals]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({ orderNumber: '', username: '', userId: '' });
    };

    const handleReview = (withdrawal) => {
        setSelectedWithdrawal(withdrawal);
        setReviewData({ status: 'approved', notes: '' });
        setShowReviewModal(true);
    };

    const submitReview = async () => {
        setSubmitting(true);
        try {
            const response = await agentService.reviewWithdrawal(selectedWithdrawal.id, reviewData);
            if (response.success) {
                toast.success(t('withdrawal_reviewed_success', { status: reviewData.status }));
                setShowReviewModal(false);
                fetchWithdrawals();
            }
        } catch (error) {
            const msg = error.message ? t(error.message) : t('error_review_withdrawal');
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const formatAccountDetails = (details, method) => {
        if (!details) return 'N/A';
        try {
            const parsed = typeof details === 'string' ? JSON.parse(details) : details;
            if (method === 'bank') {
                return `${parsed.bankName || ''} - ${parsed.accountNumber || ''}`;
            } else if (method === 'crypto') {
                return parsed.walletAddress || details;
            }
            return typeof details === 'string' ? details : JSON.stringify(details);
        } catch (e) {
            return details;
        }
    };

    const formatNetwork = (details, method) => {
        if (method === 'bank') return t('bank_card', 'Bank Card');
        try {
            const parsed = typeof details === 'string' ? JSON.parse(details) : details;
            return parsed.network || method;
        } catch (e) {
            return method;
        }
    };

    const labelStyle = { color: 'var(--text-secondary)', marginRight: '10px', fontSize: '13px' };

    const inputStyle = { backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', fontSize: '13px', padding: '6px 12px', borderRadius: '4px' };

    const StatusBadge = ({ status }) => {
        let variant = 'secondary';
        let icon = 'bi-dash-circle';

        switch (status) {
            case 'pending': variant = 'warning'; icon = 'bi-clock-history'; break;
            case 'frozen': variant = 'info'; icon = 'bi-snow2'; break;
            case 'approved': variant = 'success'; icon = 'bi-check-circle-fill'; break;
            case 'rejected': variant = 'danger'; icon = 'bi-x-circle-fill'; break;
            default: break;
        }

        return (
            <span className={`badge rounded-pill bg-${variant} bg-opacity-25 text-${variant} d-inline-flex align-items-center gap-1 border border-${variant} border-opacity-25`}>
                <i className={`bi ${icon}`}></i>
                <span className="fw-normal text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>{t(status)}</span>
            </span>
        );
    };

    return (
        <div className="majestic-page-wrapper">
            {/* Desktop Filter Section */}
            <div className="majestic-card filter-bar mb-3">
                <Row className="g-3 align-items-center w-100">
                    <Col xs={12} md="auto">
                        <div className="d-flex align-items-center">
                            <span style={{ ...labelStyle, minWidth: '80px' }}>{t('recharge_order_no')}</span>
                            <Form.Control
                                type="text"
                                name="orderNumber"
                                placeholder={t('please_enter')}
                                value={filters.orderNumber}
                                onChange={handleFilterChange}
                                className="w-100"
                                style={{ ...inputStyle, minWidth: '150px', width: 'auto' }}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md="auto">
                        <div className="d-flex align-items-center">
                            <span style={{ ...labelStyle, minWidth: '80px' }}>{t('username')}</span>
                            <Form.Control
                                type="text"
                                name="username"
                                placeholder={t('enter_username')}
                                value={filters.username}
                                onChange={handleFilterChange}
                                className="w-100"
                                style={{ ...inputStyle, minWidth: '150px', width: 'auto' }}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md="auto">
                        <div className="d-flex align-items-center">
                            <span style={{ ...labelStyle, minWidth: '80px' }}>{t('user_id')}</span>
                            <Form.Control
                                type="text"
                                name="userId"
                                placeholder={t('enter_user_id')}
                                value={filters.userId}
                                onChange={handleFilterChange}
                                className="w-100"
                                style={{ ...inputStyle, minWidth: '150px', width: 'auto' }}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md="auto" className="ms-auto text-end">
                        <div className="d-flex gap-2 justify-content-end">
                            <Button variant="primary" size="sm" className="px-4" onClick={() => fetchWithdrawals(1)}>{t('query')}</Button>
                            <Button variant="secondary" size="sm" className="px-4" onClick={clearFilters}>{t('reset')}</Button>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Mobile Filter Trigger */}
            <div className="filter-drawer-btn d-md-none" onClick={() => setShowFilterDrawer(true)}>
                <i className="bi bi-funnel"></i>
            </div>

            {/* Mobile Filter Drawer */}
            <Offcanvas
                show={showFilterDrawer}
                onHide={() => setShowFilterDrawer(false)}
                placement="bottom"
                className="majestic-card border-top border-secondary h-auto rounded-top-4"
                style={{ maxHeight: '85vh', background: 'var(--bg-card)' }}
            >
                <Offcanvas.Header closeButton className="border-bottom border-light border-opacity-10">
                    <Offcanvas.Title className="fw-bold fs-5">{t('filter_options')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-4">
                    <Form className="d-flex flex-column gap-3">
                        <div>
                            <label className="text-secondary small mb-1">{t('recharge_order_no')}</label>
                            <Form.Control
                                size="lg"
                                type="text"
                                name="orderNumber"
                                placeholder={t('please_enter')}
                                value={filters.orderNumber}
                                onChange={handleFilterChange}
                                className="bg-dark text-white border-secondary"
                            />
                        </div>
                        <div>
                            <label className="text-secondary small mb-1">{t('username')}</label>
                            <Form.Control
                                size="lg"
                                type="text"
                                name="username"
                                placeholder={t('enter_username')}
                                value={filters.username}
                                onChange={handleFilterChange}
                                className="bg-dark text-white border-secondary"
                            />
                        </div>
                        <div>
                            <label className="text-secondary small mb-1">{t('user_id')}</label>
                            <Form.Control
                                size="lg"
                                type="text"
                                name="userId"
                                placeholder={t('enter_user_id')}
                                value={filters.userId}
                                onChange={handleFilterChange}
                                className="bg-dark text-white border-secondary"
                            />
                        </div>
                        <div className="d-flex gap-2 mt-3">
                            <Button variant="secondary" size="lg" className="flex-grow-1" onClick={clearFilters}>{t('reset')}</Button>
                            <Button variant="primary" size="lg" className="flex-grow-1 fw-bold" onClick={() => { fetchWithdrawals(1); setShowFilterDrawer(false); }}>{t('query')}</Button>
                        </div>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Mobile Card List View */}
            <div className="mobile-card-list">
                {withdrawals.length === 0 ? (
                    <div className="text-center py-5 text-secondary">{t('no_data')}</div>
                ) : (
                    withdrawals.map(w => {
                        const user = w.User || {};
                        const userName = user.username || t('unknown');

                        return (
                            <div key={w.id} className="majestic-mobile-card">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="rounded-circle bg-warning bg-opacity-10 text-warning d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                            <i className="bi bi-wallet2 fs-5"></i>
                                        </div>
                                        <div>
                                            <div className="fw-bold text-white">{userName}</div>
                                            <div className="text-secondary extra-small">TX{w.id.toUpperCase().slice(0, 8)}...</div>
                                        </div>
                                    </div>
                                    <StatusBadge status={w.status} />
                                </div>

                                <div className="card-row">
                                    <span className="card-label">{t('withdrawal_order_no')}</span>
                                    <span className="card-value font-monospace extra-small">TX{w.id.toUpperCase()}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">{t('user_id')}</span>
                                    <span className="card-value font-monospace">{(w.User?.id || w.userId)?.slice(-8).toUpperCase()}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">{t('withdrawal_amount')}</span>
                                    <span className="card-value h4 mb-0 text-white">
                                        {(w.User?.currency && currencyMap[w.User.currency] !== undefined) ? currencyMap[w.User.currency] : (w.User?.currency === 'None' ? '' : '$')}
                                        {w.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">{t('wallet_address')}</span>
                                    <span className="card-value font-monospace small text-end text-break" style={{ maxWidth: '60%' }}>
                                        {formatAccountDetails(w.accountDetails, w.method)}
                                    </span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">{t('wallet_network')}</span>
                                    <span className="card-value small text-secondary">{formatNetwork(w.accountDetails, w.method)}</span>
                                </div>
                                <div className="card-row">
                                    <span className="card-label">{t('submission_time')}</span>
                                    <span className="card-value small text-secondary">{new Date(user.createdAt).toLocaleString('en-GB', { hour12: false })}</span>
                                </div>

                                {w.status === 'pending' && user.assignedAgentId === currentAgent?.id && (
                                    <div className="mt-3 pt-3 border-top border-secondary border-opacity-10">
                                        <Button
                                            variant="primary"
                                            className="w-100 rounded-pill fw-bold"
                                            onClick={() => handleReview(w)}
                                        >
                                            {t('review')}
                                        </Button>
                                        <div className="text-center extra-small text-secondary mt-2" style={{ fontSize: '11px' }}>
                                            {t('subordinated_to', 'Subordinated to')}: {user.assignedAgent?.username || t('admin')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Desktop Data Table */}
            <div className="majestic-card p-0 majestic-table-container">
                <Table hover className="border-0 mb-0">
                    <thead>
                        <tr>
                            <th>{t('username')}</th>
                            <th>{t('user_id')}</th>
                            <th>{t('withdrawal_order_no')}</th>
                            <th>{t('withdrawal_amount')}</th>
                            <th>{t('wallet_address')}</th>
                            <th>{t('wallet_network')}</th>
                            <th>{t('withdrawal_status')}</th>
                            <th>{t('submission_time')}</th>
                            <th className="text-end">{t('operate')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {withdrawals.map((w) => {
                            const user = w.User || {};
                            const userName = user.username || t('unknown');
                            const userIdDisplay = (user.id || w.userId)?.slice(-8).toUpperCase() || 'N/A';

                            return (
                                <tr key={w.id}>
                                    <td style={{ color: 'var(--text-primary)' }}>{userName}</td>
                                    <td style={{ color: 'var(--text-primary)' }}>{userIdDisplay}</td>
                                    <td style={{ color: 'var(--text-primary)' }}>TX{w.id.toUpperCase()}</td>
                                    <td style={{ color: 'var(--text-primary)' }}>
                                        {(w.User?.currency && currencyMap[w.User.currency] !== undefined) ? currencyMap[w.User.currency] : (w.User?.currency === 'None' ? '' : '$')}
                                        {w.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td style={{ color: 'var(--text-primary)' }}>{formatAccountDetails(w.accountDetails, w.method)}</td>
                                    <td style={{ color: 'var(--text-primary)' }}>{formatNetwork(w.accountDetails, w.method)}</td>
                                    <td>
                                        <span style={{ color: 'var(--text-secondary)' }}>
                                            {w.status === 'pending' ? t('pending_review') :
                                                w.status === 'frozen' ? t('frozen') :
                                                    w.status === 'approved' ? t('withdrawal_successful') :
                                                        w.status === 'rejected' ? t('refusal_to_withdraw') :
                                                            w.status || t('unknown')}
                                        </span>
                                    </td>
                                    <td style={{ color: 'var(--text-primary)' }}>{new Date(w.createdAt).toLocaleString('en-GB', { hour12: false })}</td>
                                    <td className="text-end">
                                        {w.status === 'pending' ? (
                                            user.assignedAgentId === currentAgent?.id ? (
                                                <>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="px-3 rounded-pill"
                                                        onClick={() => handleReview(w)}
                                                        style={{ fontSize: '12px' }}
                                                    >
                                                        <i className="bi bi-shield-check me-1"></i>
                                                        {t('review')}
                                                    </Button>
                                                    <div className="extra-small text-secondary mt-1" style={{ fontSize: '10px' }}>
                                                        {t('subordinated_to', 'Subordinated to')}: {user.assignedAgent?.username || t('admin')}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="d-flex flex-column align-items-end gap-1">
                                                    <span className="badge rounded-pill bg-warning-muted text-warning" style={{ fontWeight: 'normal' }}>
                                                        {t('pending_review')}
                                                    </span>
                                                    <div className="extra-small text-secondary" style={{ fontSize: '10px' }}>
                                                        {t('subordinated_to', 'Subordinated to')}: {user.assignedAgent?.username || t('admin')}
                                                    </div>
                                                </div>
                                            )
                                        ) : w.status === 'frozen' ? (
                                            <span className="badge rounded-pill bg-info-muted text-info" style={{ fontWeight: 'normal' }}>
                                                {t('frozen')}
                                            </span>
                                        ) : (
                                            <span className={`badge rounded-pill ${w.status === 'rejected' ? 'bg-danger-muted text-danger' : 'bg-success-muted text-success'}`} style={{ fontWeight: 'normal' }}>
                                                {w.status === 'rejected' ? t('rejected') : t('completed')}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {withdrawals.length === 0 && (
                            <tr>
                                <td colSpan="9" className="text-center py-5 text-secondary border-0">{t('no_data')}</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3 px-2 mb-5 mb-md-0">
                <div className="text-secondary small">
                    {t('showing_results_for')} {withdrawals.length} {t('items')}
                </div>
                <Pagination size="sm" className="mb-0">
                    <Pagination.First onClick={() => fetchWithdrawals(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => fetchWithdrawals(currentPage - 1)} disabled={currentPage === 1} />
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                            <Pagination.Item key={pageNum} active={pageNum === currentPage} onClick={() => fetchWithdrawals(pageNum)}>
                                {pageNum}
                            </Pagination.Item>
                        );
                    })}
                    {totalPages > 5 && <Pagination.Ellipsis disabled />}
                    <Pagination.Next onClick={() => fetchWithdrawals(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} />
                    <Pagination.Last onClick={() => fetchWithdrawals(totalPages)} disabled={currentPage === totalPages || totalPages === 0} />
                </Pagination>
            </div>

            {/* Review Modal */}
            <Modal
                show={showReviewModal}
                onHide={() => setShowReviewModal(false)}
                centered
                contentClassName="majestic-modal-glass border-0"
                backdrop="static"
            >
                <Modal.Header sticky="top" className="border-0 px-4 pt-4 pb-0 position-relative">
                    <Modal.Title className="majestic-header-title text-warning" style={{ fontSize: '16px', letterSpacing: '3px' }}>
                        {t('audit_withdrawal')}
                    </Modal.Title>
                    <Button
                        variant="link"
                        onClick={() => setShowReviewModal(false)}
                        className="majestic-modal-close text-white-50 shadow-none"
                        style={{ position: 'absolute', top: '12px', right: '12px' }}
                    >
                        <i className="bi bi-x-lg"></i>
                    </Button>
                </Modal.Header>
                <Modal.Body className="px-4 py-4">
                    {selectedWithdrawal && (
                        <>
                            <div className="majestic-balance-card mb-4">
                                <div>
                                    <div className="text-secondary small fw-bold mb-1" style={{ letterSpacing: '1px' }}>{t('withdrawal_amount')}</div>
                                    <div className="h3 mb-0 text-warning" style={{ fontWeight: '800' }}>
                                        {(selectedWithdrawal.User?.currency && currencyMap[selectedWithdrawal.User.currency] !== undefined) ? currencyMap[selectedWithdrawal.User.currency] : (selectedWithdrawal.User?.currency === 'None' ? '' : '$')}
                                        {(selectedWithdrawal.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                </div>
                                <div className="text-end">
                                    <div className="text-secondary small fw-bold mb-1" style={{ letterSpacing: '1px' }}>{t('user')}</div>
                                    <div className="fw-bold text-white">{selectedWithdrawal.User?.username}</div>
                                </div>
                            </div>

                            <div className="majestic-form-group">
                                <div className="majestic-label-row">
                                    <span className="majestic-label-dot"></span>
                                    <span className="majestic-label-text">{t('audit_result')}</span>
                                </div>
                                <div className="d-flex gap-2">
                                    <div
                                        className={`majestic-radio-option flex-grow-1 add ${reviewData.status === 'approved' ? 'active' : ''}`}
                                        onClick={() => setReviewData({ ...reviewData, status: 'approved' })}
                                    >
                                        <i className={`bi ${reviewData.status === 'approved' ? 'bi-check-circle-fill' : 'bi-check-circle'}`}></i>
                                        {t('pass')}
                                    </div>
                                    <div
                                        className={`majestic-radio-option flex-grow-1 info ${reviewData.status === 'frozen' ? 'active' : ''}`}
                                        onClick={() => setReviewData({ ...reviewData, status: 'frozen' })}
                                        style={reviewData.status === 'frozen' ? { background: 'rgba(13, 202, 240, 0.1)', borderColor: '#0dcaf0', color: '#0dcaf0', boxShadow: '0 0 20px rgba(13, 202, 240, 0.1)' } : {}}
                                    >
                                        <i className={`bi ${reviewData.status === 'frozen' ? 'bi-snow2' : 'bi-snow'}`}></i>
                                        {t('frozen')}
                                    </div>
                                    <div
                                        className={`majestic-radio-option flex-grow-1 subtract ${reviewData.status === 'rejected' ? 'active' : ''}`}
                                        onClick={() => setReviewData({ ...reviewData, status: 'rejected' })}
                                    >
                                        <i className={`bi ${reviewData.status === 'rejected' ? 'bi-x-circle-fill' : 'bi-x-circle'}`}></i>
                                        {t('reject')}
                                    </div>
                                </div>
                            </div>

                            <div className="majestic-form-group">
                                <div className="majestic-label-row">
                                    <span className="majestic-label-dot"></span>
                                    <span className="majestic-label-text">{t('audit_remarks')}</span>
                                </div>
                                <div className="majestic-input-wrapper">
                                    <textarea
                                        id="auditRemarks"
                                        name="auditRemarks"
                                        className="form-control majestic-input-field"
                                        rows={3}
                                        placeholder={t('enter_remarks')}
                                        value={reviewData.notes}
                                        onChange={(e) => setReviewData({ ...reviewData, notes: e.target.value })}
                                        style={{ minHeight: '100px', resize: 'none' }}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 px-4 pb-4 pt-0">
                    <Button variant="link" className="text-white-50 text-decoration-none me-auto" onClick={() => setShowReviewModal(false)}>{t('cancel')}</Button>
                    <Button
                        disabled={submitting}
                        onClick={submitReview}
                        className="majestic-btn-primary px-5 py-3"
                        style={{ minWidth: '160px' }}
                    >
                        {submitting ? t('processing') : t('confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default WithdrawalManagement;
